import { Avatar, AvatarFallback, AvatarImage } from "@bowfast/ui/components/avatar";
import { Badge } from "@bowfast/ui/components/badge";
import { Button } from "@bowfast/ui/components/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@bowfast/ui/components/card";
import { type ChartConfig, ChartContainer, ChartTooltipContent } from "@bowfast/ui/components/chart";
import { cn } from "@bowfast/ui/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Link, notFound, useParams } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  AwardIcon,
  CalendarIcon,
  CompassIcon,
  MailIcon,
  MapPinIcon,
  MessageSquareIcon,
  MoreHorizontalIcon,
  Navigation2Icon,
  PenLineIcon,
  SailboatIcon,
  ShieldCheckIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis } from "recharts";
import { SkillPathTab } from "~/components/crew/SkillPathTab";
import { DetailPageSkeleton } from "~/components/shared/DetailPageSkeleton";
import { StatTile } from "~/components/shared/StatTile";
import type { OrgRole } from "~/graphql/generated";
import { fadeUp, stagger } from "~/lib/animations";
import { createSdk } from "~/lib/gql";
import { gradientForId } from "~/lib/gradients";
import { useOrg } from "~/lib/org-context";
import { initials } from "~/lib/roles";
import { buildSkillCategories } from "~/lib/skills";
import { ROLE_CONFIG } from "~/pages/crew/crew.config";
import {
  API_EXP_STATUS_MAP,
  CERT_STATUS_CONFIG,
  type Certificate,
  type CrewProfileUser,
  type CrewProfileView,
  EXP_STATUS_VARIANT,
  type Expedition,
  type MilesEntry,
  mapCertStatus,
  mapExpeditionDates,
} from "~/pages/crew/crew.types";

// ── Chart config ──────────────────────────────────────────────────────────────

const milesChartConfig: ChartConfig = {
  nm: { label: "Nautical Miles", color: "var(--color-abyss-500)" },
};

// ── Tabs ──────────────────────────────────────────────────────────────────────

const CREW_TABS = [
  { value: "overview", label: "Overview" },
  { value: "skills", label: "Assessments" },
] as const;

type CrewTabValue = (typeof CREW_TABS)[number]["value"];

// ── Page ──────────────────────────────────────────────────────────────────────

export function CrewDetailPage() {
  const auth = useAuth();
  const { orgId } = useOrg();
  const { memberId } = useParams({ strict: false }) as { memberId: string };
  const [tab, setTab] = useState<CrewTabValue>("overview");

  // ── Queries ──────────────────────────────────────────────────────────────
  const {
    data: profileData,
    isPending: profilePending,
    isError: profileError,
  } = useQuery({
    queryKey: ["crew-profile", memberId],
    queryFn: () => createSdk(auth.user?.access_token ?? "").CrewProfile({ userId: memberId }),
    enabled: !!auth.user?.access_token && !!memberId,
    staleTime: 2 * 60 * 1000,
  });

  const { data: certsData } = useQuery({
    queryKey: ["crew-certs", orgId, memberId],
    queryFn: () => createSdk(auth.user?.access_token ?? "").CrewProfileCertificates({ orgId }),
    enabled: !!auth.user?.access_token && !!orgId && !!memberId,
    staleTime: 2 * 60 * 1000,
  });

  const { data: milesData } = useQuery({
    queryKey: ["crew-miles", memberId],
    queryFn: () => createSdk(auth.user?.access_token ?? "").CrewProfileMilesAwards({ userId: memberId }),
    enabled: !!auth.user?.access_token && !!memberId,
    staleTime: 2 * 60 * 1000,
  });

  // ── Derived data ──────────────────────────────────────────────────────────
  const user: CrewProfileUser | null = profileData?.user ?? null;

  const profile = useMemo<CrewProfileView | null>(() => {
    if (!user) return null;

    const membership = user.memberships.find((m) => m.organisation.id === orgId) ?? user.memberships[0];
    const primaryRole: OrgRole = membership?.roles[0] ?? "CREW";

    const certificates: Certificate[] = (certsData?.certificates ?? [])
      .filter((c) => c.issuedTo.id === memberId)
      .map((c) => ({
        name: c.title,
        issuer: c.course?.name ?? c.expedition?.name ?? "Bowfast",
        issuedDate: new Date(c.issuedAt).toLocaleDateString("en-GB", { month: "short", year: "numeric" }),
        status: mapCertStatus(c.issuedAt, c.revokedAt),
      }));

    const expeditions: Expedition[] = user.crewMemberships.map((cm) => ({
      name: cm.expedition.name,
      role: cm.role.charAt(0) + cm.role.slice(1).toLowerCase(),
      dates: mapExpeditionDates(cm.expedition.departureDate, cm.expedition.returnDate),
      distance: "—",
      status: API_EXP_STATUS_MAP[cm.expedition.status],
      vessel: "—",
    }));

    // Build miles log from awards, grouped by year
    const milesLog: MilesEntry[] = [];
    if (milesData?.userMilesAwards.length) {
      const byYear = new Map<number, { nm: number; expeditions: Set<string> }>();
      for (const award of milesData.userMilesAwards) {
        const year = new Date(award.awardedAt).getFullYear();
        if (!byYear.has(year)) byYear.set(year, { nm: 0, expeditions: new Set() });
        const entry = byYear.get(year);
        if (!entry) continue;
        entry.nm += award.miles;
        entry.expeditions.add(award.expeditionId);
      }
      byYear.forEach((v, year) => {
        milesLog.push({ year, nm: Math.round(v.nm), expeditions: v.expeditions.size });
      });
      milesLog.sort((a, b) => b.year - a.year);
    } else if (user.totalMilesLogged > 0) {
      milesLog.push({
        year: new Date().getFullYear(),
        nm: Math.round(user.totalMilesLogged),
        expeditions: user.crewMemberships.length,
      });
    }

    const joinedDate = membership
      ? new Date(membership.createdAt).toLocaleDateString("en-GB", { month: "long", year: "numeric" })
      : "—";

    return {
      id: user.id,
      name: user.displayName,
      initials: initials(user.firstName, user.lastName),
      role: primaryRole,
      email: user.email,
      location: user.homePort ?? "—",
      bio: user.bio ?? "",
      joinedDate,
      gradient: gradientForId(user.id),
      totalMiles: user.sailingMiles?.totalMiles ?? user.totalMilesLogged,
      totalExpeditions: user.sailingMiles?.expeditionCount ?? user.crewMemberships.length,
      totalCerts: certificates.length,
      certificates,
      expeditions,
      milesLog,
    };
  }, [user, certsData, milesData, memberId, orgId]);

  const skillCategories = useMemo(() => buildSkillCategories(user?.skillAssessments ?? []), [user]);

  const totalSkillsPassed = skillCategories.reduce((s, c) => s + c.passed, 0);
  const totalSkillsTotal = skillCategories.reduce((s, c) => s + c.total, 0);

  // ── Loading / error ───────────────────────────────────────────────────────
  if (profilePending) return <DetailPageSkeleton />;

  if (profileError || !profile) throw notFound();

  const roleConfig = ROLE_CONFIG[profile.role];

  return (
    <div className="mx-auto w-full max-w-350 space-y-6 p-4 md:p-6">
      {/* ── Back nav ── */}
      <motion.div {...fadeUp}>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-muted-foreground"
          nativeButton={false}
          render={<Link to="/crew" />}
        >
          <ArrowLeftIcon className="size-3.5" />
          Back to crew
        </Button>
      </motion.div>

      {/* ── Hero card ── */}
      <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.04 }}>
        <Card>
          <CardContent className="px-6 py-6">
            <div className="flex flex-col gap-5 md:flex-row md:items-start md:gap-6">
              <div className={cn("shrink-0 rounded-full bg-linear-to-br p-0.75", profile.gradient)}>
                <Avatar size="default" className="size-20 border-[3px] border-card">
                  {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt={profile.name} />}
                  <AvatarFallback className="bg-abyss-900 font-heading text-xl font-bold text-white">
                    {profile.initials}
                  </AvatarFallback>
                </Avatar>
              </div>

              <div className="flex flex-1 flex-col gap-3 md:flex-row md:items-start md:justify-between">
                {/* Name + role + meta */}
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <h1 className="font-heading text-xl font-bold tracking-tight md:text-2xl">{profile.name}</h1>
                    <Badge variant={roleConfig.variant} className="text-[0.6875rem]">
                      {roleConfig.label}
                    </Badge>
                  </div>
                  {profile.bio && (
                    <p className="mt-1.5 max-w-lg text-sm leading-relaxed text-muted-foreground">{profile.bio}</p>
                  )}
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
                    {profile.location !== "—" && (
                      <span className="inline-flex items-center gap-1.5">
                        <MapPinIcon className="size-3" />
                        {profile.location}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5">
                      <MailIcon className="size-3" />
                      {profile.email}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <CalendarIcon className="size-3" />
                      Joined {profile.joinedDate}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex shrink-0 gap-2">
                  <Button variant="outline" size="sm" className="gap-2">
                    <MessageSquareIcon className="size-3.5" />
                    Message
                  </Button>
                  <Button variant="outline" size="sm" className="gap-2">
                    <PenLineIcon className="size-3.5" />
                    Edit
                  </Button>
                  <Button variant="ghost" size="icon-sm">
                    <MoreHorizontalIcon className="size-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* ── Stats row ── */}
      <motion.div
        className="grid grid-cols-2 gap-4 lg:grid-cols-4"
        initial="initial"
        animate="animate"
        variants={stagger}
      >
        <motion.div variants={fadeUp}>
          <StatTile
            label="Total miles"
            value={Math.round(profile.totalMiles).toLocaleString()}
            sub="nm sailed"
            Icon={Navigation2Icon}
            iconColor="text-abyss-500"
            accentClass="bg-abyss-500"
          />
        </motion.div>
        <motion.div variants={fadeUp}>
          <StatTile
            label="Expeditions"
            value={String(profile.totalExpeditions)}
            sub="voyages completed"
            Icon={CompassIcon}
            iconColor="text-shallow-600"
            accentClass="bg-shallow-500"
          />
        </motion.div>
        <motion.div variants={fadeUp}>
          <StatTile
            label="Certifications"
            value={String(profile.totalCerts)}
            sub={`${profile.certificates.filter((c) => c.status === "issued").length} current`}
            Icon={AwardIcon}
            iconColor="text-signal-600"
            accentClass="bg-signal-500"
          />
        </motion.div>
        <motion.div variants={fadeUp}>
          <StatTile
            label="Member since"
            value={profile.joinedDate.split(" ")[1] ?? "—"}
            sub={profile.joinedDate}
            Icon={ShieldCheckIcon}
            iconColor="text-violet-500"
            accentClass="bg-violet-500"
          />
        </motion.div>
      </motion.div>

      {/* ── Tab bar ── */}
      <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.14 }}>
        <div className="flex items-center gap-1 border-b border-border">
          {CREW_TABS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setTab(value)}
              className={cn(
                "relative px-4 py-2.5 text-sm font-medium transition-colors",
                tab === value
                  ? "text-foreground after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:rounded-full after:bg-abyss-500"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {label}
              {value === "skills" && totalSkillsTotal > 0 && (
                <span className="ml-2 rounded-full bg-abyss-50 px-1.5 py-0.5 text-[0.5625rem] font-bold tabular-nums text-abyss-600 dark:bg-abyss-900 dark:text-abyss-300">
                  {totalSkillsPassed}/{totalSkillsTotal}
                </span>
              )}
            </button>
          ))}
        </div>
      </motion.div>

      {/* ── Tab content ── */}
      <AnimatePresence mode="wait">
        {tab === "overview" ? (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
          >
            {/* ── Two-column layout ── */}
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
              {/* ── Left column (2/3) ── */}
              <div className="space-y-5 lg:col-span-2">
                {/* Certifications */}
                <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.16 }}>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="font-heading text-[0.9375rem] font-semibold">Certifications</CardTitle>
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground">
                        View all
                        <ArrowRightIcon data-icon="inline-end" />
                      </Button>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {profile.certificates.length === 0 ? (
                        <p className="py-6 text-center text-sm text-muted-foreground">
                          No Bowfast-issued certificates yet.
                        </p>
                      ) : (
                        <div className="divide-y divide-border">
                          {profile.certificates.map((cert) => {
                            const statusCfg = CERT_STATUS_CONFIG[cert.status];
                            const StatusIcon = statusCfg.Icon;
                            return (
                              <div key={cert.name} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                                <div
                                  className={cn(
                                    "flex size-9 shrink-0 items-center justify-center rounded-lg",
                                    cert.status === "expired"
                                      ? "bg-coral-100 dark:bg-coral-500/10"
                                      : "bg-abyss-50 dark:bg-abyss-800",
                                  )}
                                >
                                  <AwardIcon
                                    className={cn(
                                      "size-4",
                                      cert.status === "expired" ? "text-coral-500" : "text-abyss-500",
                                    )}
                                  />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-sm font-semibold">{cert.name}</p>
                                  <p className="truncate text-xs text-muted-foreground">
                                    {cert.issuer} · {cert.issuedDate}
                                  </p>
                                </div>
                                <div className="flex shrink-0 items-center gap-1.5">
                                  <StatusIcon className={cn("size-3.5", statusCfg.color)} />
                                  <span className={cn("text-xs font-medium", statusCfg.color)}>{statusCfg.label}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>

                {/* Expedition history */}
                <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }}>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="font-heading text-[0.9375rem] font-semibold">Expedition History</CardTitle>
                      <span className="text-xs text-muted-foreground">{profile.expeditions.length} voyages</span>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {profile.expeditions.length === 0 ? (
                        <p className="py-6 text-center text-sm text-muted-foreground">No expeditions yet.</p>
                      ) : (
                        <div className="divide-y divide-border">
                          {profile.expeditions.map((exp) => (
                            <div
                              key={exp.name + exp.dates}
                              className="group flex items-center gap-4 py-3 first:pt-0 last:pb-0"
                            >
                              <div className="flex size-11 shrink-0 flex-col items-center justify-center rounded-lg bg-muted">
                                <SailboatIcon className="size-4 text-muted-foreground" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="truncate text-sm font-semibold">{exp.name}</p>
                                  <Badge
                                    variant={EXP_STATUS_VARIANT[exp.status]}
                                    {...(exp.status === "active" ? { dotted: true, pulse: true } : {})}
                                    className="text-[0.625rem]"
                                  >
                                    {exp.status === "active"
                                      ? "Active"
                                      : exp.status === "completed"
                                        ? "Completed"
                                        : exp.status === "planning"
                                          ? "Planning"
                                          : "Open"}
                                  </Badge>
                                </div>
                                <p className="mt-0.5 text-xs text-muted-foreground">
                                  {exp.role} · {exp.dates}
                                </p>
                              </div>
                              <ArrowRightIcon className="size-3.5 shrink-0 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground/60" />
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              </div>

              {/* ── Right column (1/3) ── */}
              <div className="space-y-5">
                {profile.milesLog.length > 0 && (
                  <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.18 }}>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="font-heading text-[0.9375rem] font-semibold">Miles Over Time</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <ChartContainer config={milesChartConfig} className="aspect-auto h-32 w-full">
                          <BarChart
                            data={profile.milesLog}
                            barCategoryGap="20%"
                            margin={{ top: 8, right: 0, bottom: 0, left: 0 }}
                          >
                            <CartesianGrid vertical={false} strokeDasharray="3 3" />
                            <XAxis
                              dataKey="year"
                              tickLine={false}
                              axisLine={false}
                              tickMargin={8}
                              tick={{ fill: "var(--color-muted-foreground)", fontSize: 12, fontWeight: 500 }}
                            />
                            <Tooltip content={<ChartTooltipContent />} />
                            <Bar dataKey="nm" fill="var(--color-abyss-500)" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ChartContainer>
                      </CardContent>
                      <CardFooter className="flex items-center justify-between border-t px-6 py-3">
                        <span className="text-xs text-muted-foreground">Avg. per year</span>
                        <span className="font-heading text-sm font-bold tabular-nums">
                          {Math.round(
                            profile.milesLog.reduce((s, e) => s + e.nm, 0) / profile.milesLog.length,
                          ).toLocaleString()}{" "}
                          nm
                        </span>
                      </CardFooter>
                    </Card>
                  </motion.div>
                )}

                <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.22 }}>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="font-heading text-[0.9375rem] font-semibold">Details</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <dl className="space-y-3">
                        {(
                          [
                            { label: "Organisation role", value: roleConfig.label },
                            { label: "Email", value: profile.email },
                            { label: "Location", value: profile.location },
                            { label: "Member since", value: profile.joinedDate },
                          ] as const
                        ).map((item) => (
                          <div key={item.label} className="flex items-center justify-between gap-4">
                            <dt className="text-xs text-muted-foreground">{item.label}</dt>
                            <dd className="truncate text-right text-sm font-medium">{item.value}</dd>
                          </div>
                        ))}
                      </dl>
                    </CardContent>
                  </Card>
                </motion.div>

                <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.24 }}>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="font-heading text-[0.9375rem] font-semibold">Actions</CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-2 pt-0">
                      <Button size="sm" className="w-full gap-2">
                        <AwardIcon className="size-3.5" />
                        Issue certificate
                      </Button>
                      <Button variant="outline" size="sm" className="w-full gap-2">
                        <SailboatIcon className="size-3.5" />
                        Assign to expedition
                      </Button>
                      <Button variant="ghost" size="sm" className="w-full gap-2 text-coral-500 hover:text-coral-600">
                        Remove from organisation
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="skills"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
          >
            <SkillPathTab categories={skillCategories} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
